import {logger, ObjUtil, Util} from 'yougile-util'
import {Connections} from './connections'
import {noBoardContainerId, rootNodeId} from './constants'
import {DataObject, DataObjectId, DataObjectJSON} from './data-object'
import {Entity} from './entity'
import {Hub, isDeletedEntity} from './hubs'
import {Tag} from './tag'
import {User, UserPrivileges} from './user'

export interface SandboxJSON {
  companyId: DataObjectId
  revision: number
  index: {[id: string]: DataObjectJSON}
  users: {[id: string]: UserPrivileges}

  /**
   * If not all objects are included in index then
   * this field is defined
   */
  indexPaging?: {
    page: number
    total: number
  }
}

export interface SandboxJSONDump {
  companyId: DataObjectId
  revision: number
  index: Array<{id: string; value: string /*DataObjectJSON*/}>
  users: Array<{id: string; value: UserPrivileges}>
}

export interface SandboxDump {
  companyId: DataObjectId
  revision: number
  users: Array<{id: DataObjectId; value: UserPrivileges}>
  entities: Array<{value: string}> /* EntityJSON[]; */
  connFwd: Array<{id: DataObjectId; value: string /* ConnectionsJSON */}>
}

/**
 * Sandbox stores data (Entities and Connections) available to user in the
 * corresponding sandbox (identified by companyId) and allows to do basic
 * manipulations with this data.
 */
export class Sandbox {
  public static initializer: (sandbox: Sandbox, obj: DataObject) => void

  public companyId: DataObjectId
  public revision: number
  public users: Map<DataObjectId, UserPrivileges>
  public entities: {[id: string]: Entity}
  protected connFwd: Map<DataObjectId, Connections>

  private static getParentTaskIds?: (id: DataObjectId) => DataObjectId[]
  static init(opts: {
    getParentTaskIds(id: DataObjectId): DataObjectId[]
  }): void {
    this.getParentTaskIds = opts.getParentTaskIds
  }

  constructor(
    companyId: DataObjectId,
    revision: number,
    users: Map<DataObjectId, UserPrivileges>,
    entities: {[id: string]: Entity},
    connArr: Connections[]
  ) {
    this.companyId = companyId
    this.connFwd = new Map()
    this.users = users
    this.revision = revision
    this.entities = entities
    if (!Sandbox.initializer) {
      throw new Error('Sandbox need to have defined initializer')
    }
    this.setAllValues(revision, users, entities, connArr)
  }

  public getEntitiesCount(): number {
    return Object.keys(this.entities).length
  }

  public getLazyEntitiesCount(): number {
    return Object.values(this.entities).filter(e => e.isLazy).length
  }

  public setAllValues(
    revision: number,
    users: Map<DataObjectId, UserPrivileges>,
    entities: {[id: string]: Entity},
    connArr: Connections[]
  ): void {
    this.users = users
    this.revision = revision
    this.entities = entities
    this.forAllObjects(obj => Sandbox.initializer(this, obj))
    this.connFwd.clear()
    connArr.forEach(c => this.importForwardConnection(c))
  }

  public updateUsers(usersDict: SandboxJSON['users']): void {
    const users: Map<DataObjectId, UserPrivileges> = new Map()
    Object.keys(usersDict).forEach(id => {
      users.set(id as DataObjectId, usersDict[id])
    })
    this.users = users
  }

  public get(id: DataObjectId): Entity | undefined {
    const res = this.entities[id]
    if (res) {
      return res
    }
    return undefined
  }

  public getUser(id: DataObjectId | string): User | undefined {
    const ent = this.entities[id]
    return ent instanceof User ? ent : undefined
  }

  public getTag(id: DataObjectId | string): Tag | undefined {
    const ent = this.entities[id]
    return ent instanceof Tag ? ent : undefined
  }

  public getHub(id: DataObjectId | string | undefined): Hub | undefined {
    if (!id) {
      return undefined
    }
    const ent = this.entities[id]
    return ent instanceof Hub ? ent : undefined
  }

  public has(id: DataObjectId): boolean {
    return !!this.entities[id]
  }

  public add(ent: Entity, hostId: DataObjectId): void {
    ent.companyId = this.companyId
    ent.location = hostId
    this.entities[ent.id] = ent
    Sandbox.initializer(this, ent)
  }

  public isDeleted(id: DataObjectId, recur = 0): boolean {
    if (recur > 7) {
      return false
    }
    const {companyId} = this
    let curObj = this.get(id)
    if (!curObj || isDeletedEntity(curObj)) {
      return true
    }
    if (curObj.location !== noBoardContainerId) {
      let curId = id
      let level = 0
      while (curId && curId !== companyId && curObj && level < 5) {
        curId = curObj.location
        curObj = this.get(curId)
        if (isDeletedEntity(curObj)) {
          return true
        }
        level++
      }
    } else {
      const parents = Sandbox.getParentTaskIds?.(id) ?? []
      if (parents.length) {
        for (const parent of parents) {
          if (!this.isDeleted(parent, recur + 1)) {
            return false
          }
        }
        return true
      }
    }
    return false
  }

  public forAllObjects(handler: (obj: DataObject) => void): void {
    ObjUtil.forEach(this.entities, (_id, ent) => handler(ent))
    this.connFwd.forEach(handler)
  }

  public initializeEmptyConnections(from: DataObjectId): void {
    const fromEntity = this.get(from)
    if (
      !fromEntity ||
      !(fromEntity instanceof Tag || fromEntity instanceof User)
    ) {
      throw new Error('Incorrect from for connection')
    }
    if (this.connFwd.has(from)) {
      throw new Error('Already initialized')
    }
    const newConn = new Connections(this.companyId, from)
    newConn.revision = 1
    Sandbox.initializer(this, newConn)
    this.connFwd.set(from, newConn)
    newConn.commit()
  }

  public importForwardConnection(
    conn: Connections,
    needInitialization = true
  ): void {
    const fromEntity = this.get(conn.from)
    if (
      !fromEntity ||
      !(fromEntity instanceof Tag || fromEntity instanceof User)
    ) {
      if (typeof fromEntity === 'undefined') {
        return
      } else {
        logger.warn('Incorrect from for connection')
        return
      }
    }
    if (needInitialization) {
      Sandbox.initializer(this, conn)
    }
    if (!this.get(conn.from)) {
      logger.warn('!no from for connection')
    }
    const old = this.connFwd.get(conn.from)
    if (old) {
      logger.warn(
        'Duplicate connections',
        conn.from && this.get(conn.from) && this.get(conn.from)?.title,
        conn.id
      )
      if (conn.timestamp < old.timestamp) {
        this.connFwd.set(conn.from, conn)
        logger.log('Set older connection', conn)
      }
    } else {
      this.connFwd.set(conn.from, conn)
    }
  }

  public connect(from: DataObjectId, to: DataObjectId): void {
    const fromEntity = this.get(from)
    if (
      !fromEntity ||
      !(fromEntity instanceof Tag || fromEntity instanceof User)
    ) {
      throw new Error('Incorrect from for connection')
    }
    if (!this.connFwd.has(from)) {
      this.initializeEmptyConnections(from)
    }
    this.connFwd.get(from)?.add(to)
  }

  public getConnectionFrom(from: DataObjectId): Connections | undefined {
    return from ? this.connFwd.get(from) : undefined
  }

  public disconnect(from: DataObjectId, to: DataObjectId): void {
    const fwd = this.connFwd.get(from)
    if (fwd) {
      fwd.disconnect(to)
      fwd.commit()
    }
  }

  public isParentOf(parentId: DataObjectId, childId: DataObjectId): boolean {
    if (!parentId || !childId || childId === rootNodeId) {
      return false
    }
    if (parentId === rootNodeId) {
      return true
    }
    const parent = this.get(parentId)
    const child = this.get(childId)
    if (!parent || !child) {
      return false
    }
    if (parentId === child.location) {
      return true
    } else {
      return this.isParentOf(parentId, child.location)
    }
  }

  public removeConnections(from: DataObjectId): void {
    const fwd = this.connFwd.get(from)
    if (!fwd) {
      return
    }
    fwd.disconnectAll()
    fwd.commit()
  }

  public getModifiers(from: DataObjectId, to: DataObjectId): string[] {
    return this.connFwd.get(from)?.getModifiers(to) ?? []
  }

  public addModifiers(
    from: DataObjectId,
    to: DataObjectId,
    modifiers: string[],
    commit = true
  ): void {
    Util.assert(this.isConnected(from, to), 'The connection must exist')
    const fromConn = this.connFwd.get(from)
    fromConn?.addModifiers(to, modifiers)
    if (fromConn && commit) {
      fromConn.commit()
    }
  }

  public removeModifiers(
    from: DataObjectId,
    to: DataObjectId,
    modifiers: string[]
  ): void {
    Util.assert(this.isConnected(from, to), 'The connection must exist')
    const fwdConn = this.connFwd.get(from)
    fwdConn?.removeModifiers(to, modifiers)
    fwdConn?.commit()
  }

  public setModifiers(
    from: DataObjectId,
    to: DataObjectId,
    modifiers: string[]
  ): void {
    Util.assert(this.isConnected(from, to), 'The connection must exist')
    const fwdConn = this.connFwd.get(from)
    modifiers.forEach((m: string) => {
      Util.assert(typeof m === 'string', 'Only string modifiers are allowed')
    })
    fwdConn?.setModifiers(to, modifiers)
    fwdConn?.commit()
  }

  public forwardConnections(id: DataObjectId): Set<DataObjectId> {
    const res = this.connFwd.get(id)
    if (typeof res === 'undefined') {
      return new Set()
    }
    return res.getAllConnected()
  }

  public isConnected(id1: DataObjectId, id2: DataObjectId): boolean {
    const res = this.connFwd.get(id1)
    return res?.isConnected(id2) ?? false
  }

  public hasModifiers(id1: DataObjectId, id2: DataObjectId): boolean {
    const res = this.connFwd.get(id1)
    return res?.hasModifiers(id2) ?? false
  }

  public removeForwardConnection(conn: Connections): void {
    this.connFwd.delete(conn.from)
  }
}
